
import { EtsyOrder } from "@/types/etsy";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Package, User, MapPin, Calendar, CreditCard, Mail } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";
import { getCountryName } from "@/utils/countryUtils";

interface PrintableOrderSummaryProps {
  order: EtsyOrder;
  className?: string;
}

const PrintableOrderSummary = ({ order, className = "" }: PrintableOrderSummaryProps) => {
  const { formatPrice } = useCurrency();

  // Helper function to get location string
  const getLocationString = (order: EtsyOrder): string => {
    const parts = [];
    if (order.city) parts.push(order.city);
    if (order.country) parts.push(getCountryName(order.country));
    if (order.zip) parts.push(order.zip);
    return parts.join(', ');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "New":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Processing":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      case "Shipped":
        return "bg-green-50 text-green-700 border-green-200";
      case "Delivered":
        return "bg-gray-50 text-gray-700 border-gray-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className={`printable-order-summary print:shadow-none ${className}`}>
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            .printable-order-summary {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .print\\:shadow-none {
              box-shadow: none !important;
            }
            .print\\:border-black {
              border-color: black !important;
            }
            .print\\:text-black {
              color: black !important;
            }
          }
        `
      }} />
      
      <Card className="print:border-black print:shadow-none">
        <CardHeader className="text-center border-b print:border-black">
          <CardTitle className="text-2xl font-bold print:text-black">
            Order Summary
          </CardTitle>
          <div className="flex justify-center items-center gap-2 mt-2">
            <Badge variant="outline" className={`${getStatusColor(order.status)} print:border-black print:text-black`}>
              {order.status}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-6">
          {/* Order Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 print:text-black">
              <Package className="h-5 w-5" />
              Order Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 print:text-black font-medium">Order ID:</p>
                <p className="font-mono print:text-black">{order.receiptId}</p>
              </div>
              <div>
                <p className="text-gray-600 print:text-black font-medium">Date:</p>
                <p className="print:text-black">{order.date}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-600 print:text-black font-medium">Product:</p>
                <p className="font-semibold print:text-black">{order.productName}</p>
              </div>
              {order.trackingId && order.trackingId !== 'N/A' && (
                <div className="col-span-2">
                  <p className="text-gray-600 print:text-black font-medium">Tracking Number:</p>
                  <p className="font-mono text-sm bg-gray-100 p-2 rounded print:bg-white print:border print:border-black print:text-black">
                    {order.trackingId}
                  </p>
                </div>
              )}
            </div>
          </div>

          <Separator className="print:border-black" />

          {/* Customer Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 print:text-black">
              <User className="h-5 w-5" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 print:text-black font-medium">Name:</p>
                <p className="font-semibold print:text-black">{order.customerName}</p>
              </div>
              {order.customerEmail && (
                <div>
                  <p className="text-gray-600 print:text-black font-medium flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Email:
                  </p>
                  <p className="print:text-black">{order.customerEmail}</p>
                </div>
              )}
            </div>
          </div>

          <Separator className="print:border-black" />

          {/* Shipping Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 print:text-black">
              <MapPin className="h-5 w-5" />
              Shipping Address
            </h3>
            <div className="text-sm space-y-1">
              <p className="font-semibold print:text-black">{order.customerName}</p>
              {order.address && order.address !== 'N/A' && (
                <p className="print:text-black">{order.address}</p>
              )}
              <div className="print:text-black">
                <span className="font-medium">Full Address: </span>
                {order.city && <span>{order.city}</span>}
                {order.city && order.country && <span>, </span>}
                {order.country && <span>{getCountryName(order.country)}</span>}
                {(order.city || order.country) && order.zip && <span>, </span>}
                {order.zip && <span>{order.zip}</span>}
              </div>
            </div>
          </div>

          <Separator className="print:border-black" />

          {/* Payment Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 print:text-black">
              <CreditCard className="h-5 w-5" />
              Payment Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 print:text-black font-medium">Total Amount:</p>
                <p className="font-bold text-lg text-green-600 print:text-black">
                  {order.totalAmount && order.currencyCode 
                    ? formatPrice(order.totalAmount, order.currencyCode)
                    : 'N/A'
                  }
                </p>
              </div>
              <div>
                <p className="text-gray-600 print:text-black font-medium">Payment Method:</p>
                <p className="print:text-black">Etsy Payment</p>
              </div>
            </div>
          </div>

          {/* Supplier Notes Section */}
          <Separator className="print:border-black" />
          <div>
            <h3 className="font-semibold text-lg mb-3 print:text-black">Supplier Notes</h3>
            <div className="border border-dashed border-gray-300 rounded-md p-3 min-h-[100px] print:border-black"></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrintableOrderSummary;
